/**
 * Accounting Student Notes - Years 11-13
 * Vanuatu National Syllabus
 * Last Updated: February 3, 2026
 */

// ============================================
// IMAGE RESOURCES FOR ACCOUNTING NOTES
// ============================================
export const accountingImages = {
  accounting: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800",
  calculator: "https://images.unsplash.com/photo-1564939558297-fc396f18e5c7?w=800",
  spreadsheet: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800",
  money: "https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800",
  bank: "https://images.unsplash.com/photo-1501167786227-4cba60f6d58f?w=800",
  documents: "https://images.unsplash.com/photo-1586282391129-76a6df230234?w=800",
  financial: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800",
  analysis: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800"
};

// ==================== YEAR 11 ACCOUNTING NOTES ====================
export const year11AccountingStudentNotes = {
  strand1: {
    title: "Communicating Financial Information",
    overview: "Understanding the role of accounting in society and internal control systems for safeguarding assets.",
    image: accountingImages.accounting,
    units: [
      {
        id: "1.1",
        title: "Conceptual Basis of Accounting",
        image: accountingImages.calculator,
        objectives: [
          "Define accounting and explain its role in business",
          "State and apply the basic accounting equation",
          "Identify and define the five financial elements",
          "Distinguish between types of business organizations",
          "List and explain accounting principles and assumptions"
        ],
        keyTerms: [
          { term: "Accounting", definition: "The process of identifying, measuring, recording, and communicating financial information about an organization to users for decision-making." },
          { term: "Accounting Equation", definition: "Assets = Liabilities + Owner's Equity. The fundamental equation showing the relationship between what a business owns and owes." },
          { term: "Assets", definition: "Resources owned by the business that have future economic value (e.g., cash, inventory, equipment)." },
          { term: "Liabilities", definition: "Obligations or debts owed to others (e.g., accounts payable, loans, mortgages)." },
          { term: "Owner's Equity", definition: "The owner's claim on the assets after all liabilities are paid. Also called capital or net worth." },
          { term: "Revenue", definition: "Income earned from business operations (e.g., sales, service income, interest income)." },
          { term: "Expenses", definition: "Costs incurred to generate revenue (e.g., wages, rent, utilities, supplies)." },
          { term: "Sole Proprietorship", definition: "A business owned by one person with unlimited liability." },
          { term: "Partnership", definition: "A business owned by two or more people sharing profits and responsibilities." },
          { term: "Company", definition: "A separate legal entity with limited liability for shareholders." }
        ],
        studyNotes: `
## The Accounting Equation

The basic accounting equation is the foundation of double-entry bookkeeping:

**Assets = Liabilities + Owner's Equity**

### Expanded Form:
Assets = Liabilities + Owner's Equity + Revenue - Expenses

### The Five Financial Elements:

| Element | Definition | Examples |
|---------|-----------|----------|
| Assets | Resources owned | Cash, Inventory, Equipment |
| Liabilities | Debts owed | Loans, Accounts Payable |
| Owner's Equity | Owner's claim | Capital, Drawings |
| Revenue | Income earned | Sales, Service Revenue |
| Expenses | Costs incurred | Wages, Rent, Utilities |

## Types of Business Organizations

### Sole Proprietorship
- Owned by one person
- Easy to set up
- Full control
- Unlimited liability

### Partnership
- Owned by 2+ people
- Shared resources
- Shared profits
- Joint liability

### Company
- Separate legal entity
- Limited liability
- Complex regulations
- Perpetual existence

## Accounting Principles

1. **Business Entity Concept** - Business is separate from owner
2. **Going Concern** - Business will continue operating
3. **Historical Cost** - Assets recorded at purchase price
4. **Monetary Unit** - Transactions in money terms
5. **Accrual Basis** - Record when earned/incurred
6. **Matching Principle** - Match expenses with revenues
        `,
        practiceQuestions: [
          "Define accounting and explain its importance to business.",
          "State the basic accounting equation and identify each element.",
          "Give TWO examples each of assets, liabilities, and owner's equity.",
          "Compare the advantages and disadvantages of a sole proprietorship vs. a company.",
          "Explain why the business entity concept is important for accurate financial reporting.",
          "If Assets = $50,000 and Liabilities = $20,000, calculate Owner's Equity."
        ],
        examTips: [
          "Always remember: Assets = Liabilities + Owner's Equity",
          "Classify items correctly before recording transactions",
          "Know the advantages AND disadvantages of each business type"
        ]
      },
      {
        id: "1.2",
        title: "Internal Control",
        image: accountingImages.bank,
        objectives: [
          "Identify the six principles of internal control",
          "List internal control procedures for cash receipts and payments",
          "Explain the need for internal control over cash",
          "Relate each control procedure to a principle",
          "Implement a petty cash imprest system"
        ],
        keyTerms: [
          { term: "Internal Control", definition: "Policies and procedures designed to safeguard assets, ensure accurate records, promote efficiency, and encourage policy adherence." },
          { term: "Segregation of Duties", definition: "Dividing related duties among different people to reduce risk of errors and fraud." },
          { term: "Establishment of Responsibility", definition: "Assigning specific tasks to specific individuals to ensure accountability." },
          { term: "Documentation Procedures", definition: "Using pre-numbered source documents to track and verify all transactions." },
          { term: "Physical Controls", definition: "Safeguards to protect assets (e.g., safes, locks, passwords)." },
          { term: "Independent Verification", definition: "Regular checks by someone not involved in the original task." },
          { term: "Petty Cash", definition: "A small fund of cash kept on hand for minor expenses." },
          { term: "Imprest System", definition: "A method where petty cash is restored to a fixed amount after each period." }
        ],
        studyNotes: `
## Six Principles of Internal Control

1. **Establishment of Responsibility**
   - One person accountable per task
   - Clear job descriptions

2. **Segregation of Duties**
   - Different people handle related activities
   - Person handling cash ≠ person recording cash

3. **Documentation Procedures**
   - Pre-numbered source documents
   - All transactions documented

4. **Physical Controls**
   - Safes for cash
   - Locked storage
   - Computer passwords

5. **Independent Internal Verification**
   - Surprise cash counts
   - Regular audits

6. **Human Resource Controls**
   - Background checks
   - Mandatory vacations

## Cash Controls

### Cash Receipts Controls:
| Procedure | Purpose |
|-----------|---------|
| Issue receipts | Documentation |
| Daily deposits | Safeguard assets |
| Cash register | Record all sales |
| Separate staff | Segregation |

### Cash Payments Controls:
| Procedure | Purpose |
|-----------|---------|
| Use cheques | Documentation |
| Pre-numbered cheques | Control |
| Two signatures | Authorization |
| Petty cash | Efficiency |

## Petty Cash Imprest System

1. Establish fixed amount (e.g., 5,000 VT)
2. Make small payments from fund
3. Keep vouchers for all payments
4. Reimburse to restore original amount

### Example:
- Petty cash fund: 5,000 VT
- Payments made: 3,800 VT
- Cash remaining: 1,200 VT
- Reimbursement needed: 3,800 VT
        `,
        practiceQuestions: [
          "List the six principles of internal control.",
          "Explain why segregation of duties is important.",
          "Give THREE examples of physical controls.",
          "A petty cash fund of $500 has payments totaling $380. How much is needed to reimburse the fund?",
          "Why should the person who handles cash NOT record cash transactions?",
          "Describe the imprest system for petty cash."
        ],
        examTips: [
          "Memorize all six internal control principles",
          "Understand the PURPOSE of each control procedure",
          "Practice petty cash calculations"
        ]
      }
    ]
  },
  strand2: {
    title: "Recording Financial Information",
    overview: "Recording transactions using double-entry bookkeeping, journals, and ledgers.",
    image: accountingImages.documents,
    units: [
      {
        id: "2.1",
        title: "Double-Entry Bookkeeping",
        image: accountingImages.spreadsheet,
        objectives: [
          "Name the stages in the accounting cycle",
          "Identify source documents and their purposes",
          "Apply double-entry rules when recording transactions",
          "Prepare journal entries for business transactions",
          "Post entries to ledger accounts"
        ],
        keyTerms: [
          { term: "Accounting Cycle", definition: "The step-by-step process of recording, classifying, and summarizing financial transactions." },
          { term: "Source Document", definition: "Original record providing evidence of a transaction (e.g., invoice, receipt, cheque)." },
          { term: "Double-Entry", definition: "Every transaction affects at least two accounts - a debit and a credit of equal amounts." },
          { term: "Debit", definition: "An entry on the left side of an account. Increases assets and expenses; decreases liabilities, equity, and revenue." },
          { term: "Credit", definition: "An entry on the right side of an account. Increases liabilities, equity, and revenue; decreases assets and expenses." },
          { term: "Journal", definition: "A book of original entry where transactions are first recorded chronologically." },
          { term: "Ledger", definition: "A book containing all accounts, organized by type." },
          { term: "Trial Balance", definition: "A list of all ledger account balances to verify that debits equal credits." }
        ],
        studyNotes: `
## The Accounting Cycle

1. **Identify Transactions** - Analyze business events
2. **Record in Journals** - Enter in appropriate journals
3. **Post to Ledger** - Transfer to ledger accounts
4. **Prepare Trial Balance** - List all account balances
5. **Make Adjusting Entries** - Adjust for accruals
6. **Prepare Financial Statements** - Create reports
7. **Close Accounts** - Close temporary accounts
8. **Post-Closing Trial Balance** - Verify accuracy

## Source Documents

| Document | Purpose |
|----------|---------|
| Invoice | Records sales/purchases |
| Receipt | Confirms cash received |
| Cheque | Authorizes payment |
| Credit Note | Records returns |
| Bank Statement | Shows bank transactions |

## Double-Entry Rules

| Account Type | Increase | Decrease |
|-------------|----------|----------|
| Assets | DEBIT | Credit |
| Liabilities | Credit | DEBIT |
| Owner's Equity | Credit | DEBIT |
| Revenue | Credit | DEBIT |
| Expenses | DEBIT | Credit |

### Example Transaction:
"Received $500 cash for services"

| Account | Debit | Credit |
|---------|-------|--------|
| Cash | $500 | |
| Service Revenue | | $500 |

## The Seven Journals

1. **General Journal** - Non-routine transactions
2. **Sales Journal** - Credit sales only
3. **Sales Returns Journal** - Returns
4. **Purchases Journal** - Credit purchases
5. **Purchases Returns Journal** - Returns
6. **Cash Receipts Journal** - All cash received
7. **Cash Payments Journal** - All cash paid
        `,
        practiceQuestions: [
          "List the eight stages of the accounting cycle.",
          "Name FOUR source documents and their purposes.",
          "State the rules for debiting and crediting asset accounts.",
          "Record the journal entry: 'Purchased inventory for $2,000 cash'.",
          "Record the journal entry: 'Sold goods on credit for $1,500'.",
          "What is the purpose of a trial balance?"
        ],
        examTips: [
          "DEBIT = Left side, CREDIT = Right side",
          "Assets and Expenses increase with DEBITS",
          "Liabilities, Equity, Revenue increase with CREDITS",
          "Total debits must ALWAYS equal total credits"
        ]
      },
      {
        id: "2.2",
        title: "Bank Reconciliation",
        image: accountingImages.bank,
        objectives: [
          "Interpret a bank statement",
          "Identify reasons for differences between cash book and bank statement",
          "Prepare a bank reconciliation statement",
          "Make adjusting entries based on reconciliation"
        ],
        keyTerms: [
          { term: "Bank Reconciliation", definition: "The process of comparing the cash book balance with the bank statement balance to identify and explain differences." },
          { term: "Outstanding Cheques", definition: "Cheques issued but not yet presented to the bank for payment." },
          { term: "Deposits in Transit", definition: "Deposits made but not yet recorded by the bank." },
          { term: "Bank Charges", definition: "Fees charged by the bank (e.g., service fees, overdraft interest)." },
          { term: "Direct Debits", definition: "Automatic payments made from the account." },
          { term: "Standing Orders", definition: "Regular automatic payments for fixed amounts." },
          { term: "NSF Cheque", definition: "Not Sufficient Funds - a cheque that bounced due to insufficient funds." }
        ],
        studyNotes: `
## Why Reconcile?

The bank statement balance often differs from the cash book due to:
- Timing differences
- Bank charges not yet recorded
- Errors

## Common Differences

### Items in Cash Book, NOT in Bank Statement:
- Outstanding cheques (deduct from bank)
- Deposits in transit (add to bank)

### Items in Bank Statement, NOT in Cash Book:
- Bank charges (deduct from cash book)
- Direct credits (add to cash book)
- NSF cheques (deduct from cash book)

## Bank Reconciliation Format

\`\`\`
BANK RECONCILIATION STATEMENT
As at [Date]

Balance per bank statement         $X,XXX
Add: Deposits in transit             XXX
                                   ------
                                   X,XXX
Less: Outstanding cheques           (XXX)
                                   ------
Adjusted bank balance              $X,XXX
                                   ======

Balance per cash book              $X,XXX
Add: Direct credits                  XXX
                                   ------
                                   X,XXX
Less: Bank charges                  (XXX)
      NSF cheques                   (XXX)
                                   ------
Adjusted cash book balance         $X,XXX
                                   ======
\`\`\`

Both adjusted balances should be EQUAL!
        `,
        practiceQuestions: [
          "List THREE items that would appear in a bank statement but not in the cash book.",
          "Explain why a cheque issued yesterday might not appear on the bank statement.",
          "The bank statement shows $5,000. Outstanding cheques total $800. Deposits in transit total $500. What is the adjusted bank balance?",
          "What is an NSF cheque and how does it affect the cash book?",
          "Prepare a bank reconciliation from the following: Bank balance $4,200, Cash book balance $3,850, Outstanding cheques $500, Deposits in transit $100, Bank charges $50."
        ],
        examTips: [
          "Start by identifying which items belong to bank vs cash book adjustments",
          "Outstanding cheques REDUCE the bank balance",
          "Bank charges REDUCE the cash book balance",
          "Final adjusted balances MUST be equal"
        ]
      }
    ]
  },
  strand3: {
    title: "Reporting Financial Information",
    overview: "Preparing financial statements and understanding balance day adjustments.",
    image: accountingImages.financial,
    units: [
      {
        id: "3.1",
        title: "Balance Day Adjustments",
        image: accountingImages.documents,
        objectives: [
          "Identify balance day adjustment accounts",
          "Account for prepayments and accruals",
          "Calculate and record depreciation",
          "Prepare adjusting journal entries"
        ],
        keyTerms: [
          { term: "Balance Day Adjustments", definition: "Adjustments made at the end of an accounting period to ensure revenues and expenses are recorded in the correct period." },
          { term: "Prepaid Expenses", definition: "Expenses paid in advance (e.g., prepaid insurance, prepaid rent)." },
          { term: "Accrued Expenses", definition: "Expenses incurred but not yet paid (e.g., accrued wages, accrued interest)." },
          { term: "Unearned Revenue", definition: "Revenue received in advance before services are provided." },
          { term: "Accrued Revenue", definition: "Revenue earned but not yet received." },
          { term: "Depreciation", definition: "The systematic allocation of the cost of a non-current asset over its useful life." },
          { term: "Straight-Line Depreciation", definition: "Depreciation method that allocates an equal amount of cost each year." }
        ],
        studyNotes: `
## Types of Balance Day Adjustments

### 1. Prepaid Expenses
Expenses paid in advance need to be allocated.

**Example:** Paid $1,200 insurance for 12 months
- Monthly expense = $1,200 ÷ 12 = $100
- If 3 months used: Insurance Expense = $300
- Prepaid Insurance = $900

**Adjusting Entry:**
\`\`\`
Dr Insurance Expense    $300
   Cr Prepaid Insurance      $300
\`\`\`

### 2. Accrued Expenses
Expenses incurred but not yet paid.

**Example:** Wages of $500 owed at year end

**Adjusting Entry:**
\`\`\`
Dr Wages Expense       $500
   Cr Wages Payable         $500
\`\`\`

### 3. Unearned Revenue
Revenue received before service provided.

**Example:** Received $600 for 6 months rent in advance
- 2 months earned = $200

**Adjusting Entry:**
\`\`\`
Dr Unearned Rent       $200
   Cr Rent Revenue          $200
\`\`\`

### 4. Depreciation
Allocates asset cost over useful life.

**Straight-Line Formula:**
\`\`\`
Annual Depreciation = (Cost - Salvage Value) ÷ Useful Life
\`\`\`

**Example:** Equipment cost $10,000, salvage $1,000, life 5 years
- Depreciation = ($10,000 - $1,000) ÷ 5 = $1,800/year

**Adjusting Entry:**
\`\`\`
Dr Depreciation Expense        $1,800
   Cr Accumulated Depreciation      $1,800
\`\`\`
        `,
        practiceQuestions: [
          "Define balance day adjustments and explain why they are necessary.",
          "Prepare the adjusting entry for $600 prepaid rent when 4 months have been used.",
          "Wages of $1,200 are owing at year end. Prepare the adjusting entry.",
          "Equipment costs $20,000 with salvage value of $2,000 and useful life of 6 years. Calculate annual depreciation.",
          "Explain the difference between prepaid expenses and accrued expenses.",
          "Why is depreciation recorded as an adjusting entry?"
        ],
        examTips: [
          "Prepaid = Asset (reduces over time)",
          "Accrued = Liability (expense not yet paid)",
          "Depreciation never affects cash",
          "Use the matching principle to determine adjustments"
        ]
      },
      {
        id: "3.2",
        title: "Financial Statements",
        image: accountingImages.analysis,
        objectives: [
          "Define and prepare an Income Statement",
          "Define and prepare a Statement of Financial Position",
          "Classify assets and liabilities correctly",
          "Calculate gross profit and net profit"
        ],
        keyTerms: [
          { term: "Income Statement", definition: "A financial statement showing revenues, expenses, and profit or loss for a period." },
          { term: "Statement of Financial Position", definition: "A financial statement showing assets, liabilities, and equity at a specific date. Also called Balance Sheet." },
          { term: "Gross Profit", definition: "Sales revenue minus cost of goods sold." },
          { term: "Net Profit", definition: "Gross profit minus all operating expenses." },
          { term: "Current Assets", definition: "Assets expected to be converted to cash or used within one year." },
          { term: "Non-Current Assets", definition: "Assets expected to provide benefits beyond one year." },
          { term: "Current Liabilities", definition: "Obligations due within one year." },
          { term: "Non-Current Liabilities", definition: "Obligations due beyond one year." }
        ],
        studyNotes: `
## Income Statement Format

\`\`\`
INCOME STATEMENT
For the Year Ended [Date]

Sales Revenue                      $XX,XXX
Less: Cost of Goods Sold          (XX,XXX)
                                  --------
GROSS PROFIT                       XX,XXX

Less: Operating Expenses
  Wages Expense           X,XXX
  Rent Expense            X,XXX
  Depreciation            X,XXX
  Utilities               X,XXX
  Total Expenses                  (XX,XXX)
                                  --------
NET PROFIT                        $XX,XXX
\`\`\`

## Cost of Goods Sold Formula

\`\`\`
COGS = Opening Inventory + Purchases - Closing Inventory
\`\`\`

## Statement of Financial Position Format

\`\`\`
STATEMENT OF FINANCIAL POSITION
As at [Date]

ASSETS
Current Assets:
  Cash                    $X,XXX
  Accounts Receivable      X,XXX
  Inventory                X,XXX
  Prepaid Expenses         X,XXX
  Total Current Assets            $XX,XXX

Non-Current Assets:
  Equipment               $X,XXX
  Less: Acc. Depreciation (X,XXX)
                                   XX,XXX
TOTAL ASSETS                      $XX,XXX

LIABILITIES
Current Liabilities:
  Accounts Payable        $X,XXX
  Wages Payable            X,XXX
  Total Current Liabilities       $XX,XXX

Non-Current Liabilities:
  Bank Loan                        XX,XXX
TOTAL LIABILITIES                 $XX,XXX

OWNER'S EQUITY
Capital                   $XX,XXX
Add: Net Profit            XX,XXX
Less: Drawings            (X,XXX)
TOTAL EQUITY                      $XX,XXX

TOTAL LIABILITIES + EQUITY        $XX,XXX
\`\`\`
        `,
        practiceQuestions: [
          "Calculate gross profit if Sales = $50,000 and COGS = $30,000.",
          "Calculate COGS if Opening Inventory = $5,000, Purchases = $25,000, Closing Inventory = $6,000.",
          "Classify these items as Current or Non-Current Assets: Cash, Land, Inventory, Equipment, Accounts Receivable.",
          "What is the difference between gross profit and net profit?",
          "Prepare an Income Statement from: Sales $80,000, COGS $48,000, Wages $12,000, Rent $6,000, Utilities $2,000."
        ],
        examTips: [
          "Gross Profit = Sales - COGS",
          "Net Profit = Gross Profit - Operating Expenses",
          "Current = within 1 year; Non-Current = beyond 1 year",
          "Total Assets MUST equal Total Liabilities + Equity"
        ]
      }
    ]
  },
  strand4: {
    title: "Evaluating Financial Information",
    overview: "Analyzing financial statements using ratios and making business decisions.",
    image: accountingImages.analysis,
    units: [
      {
        id: "4.1",
        title: "Financial Ratio Analysis",
        image: accountingImages.financial,
        objectives: [
          "Calculate profitability ratios",
          "Calculate liquidity ratios",
          "Interpret ratio results",
          "Use ratios to evaluate business performance"
        ],
        keyTerms: [
          { term: "Financial Ratio", definition: "A mathematical comparison between two financial figures to assess performance, position, or efficiency." },
          { term: "Gross Profit Margin", definition: "Gross profit as a percentage of sales: (Gross Profit ÷ Sales) × 100" },
          { term: "Net Profit Margin", definition: "Net profit as a percentage of sales: (Net Profit ÷ Sales) × 100" },
          { term: "Current Ratio", definition: "Current Assets ÷ Current Liabilities - measures ability to pay short-term debts." },
          { term: "Quick Ratio", definition: "(Current Assets - Inventory) ÷ Current Liabilities - excludes inventory for stricter liquidity test." },
          { term: "Return on Equity", definition: "(Net Profit ÷ Owner's Equity) × 100 - measures return on owner's investment." }
        ],
        studyNotes: `
## Profitability Ratios

### 1. Gross Profit Margin
\`\`\`
GP Margin = (Gross Profit ÷ Sales) × 100
\`\`\`
- Shows profit before operating expenses
- Higher = better markup on goods

### 2. Net Profit Margin
\`\`\`
NP Margin = (Net Profit ÷ Sales) × 100
\`\`\`
- Shows profit after all expenses
- Higher = better overall efficiency

### 3. Return on Equity (ROE)
\`\`\`
ROE = (Net Profit ÷ Owner's Equity) × 100
\`\`\`
- Shows return on owner's investment
- Compare to bank interest rates

## Liquidity Ratios

### 1. Current Ratio
\`\`\`
Current Ratio = Current Assets ÷ Current Liabilities
\`\`\`
- Ideal: 2:1 (two dollars of assets for every dollar of liability)
- Below 1:1 = liquidity problems

### 2. Quick Ratio (Acid Test)
\`\`\`
Quick Ratio = (Current Assets - Inventory) ÷ Current Liabilities
\`\`\`
- More conservative test
- Ideal: 1:1

## Example Analysis

| Data | Amount |
|------|--------|
| Sales | $100,000 |
| Gross Profit | $40,000 |
| Net Profit | $15,000 |
| Current Assets | $30,000 |
| Inventory | $10,000 |
| Current Liabilities | $15,000 |
| Owner's Equity | $50,000 |

**Calculations:**
- GP Margin = ($40,000 ÷ $100,000) × 100 = 40%
- NP Margin = ($15,000 ÷ $100,000) × 100 = 15%
- Current Ratio = $30,000 ÷ $15,000 = 2:1
- Quick Ratio = ($30,000 - $10,000) ÷ $15,000 = 1.33:1
- ROE = ($15,000 ÷ $50,000) × 100 = 30%
        `,
        practiceQuestions: [
          "Calculate gross profit margin if Sales = $80,000 and Gross Profit = $24,000.",
          "A business has Current Assets of $45,000 and Current Liabilities of $30,000. Calculate the current ratio and comment on liquidity.",
          "Net Profit is $12,000 and Owner's Equity is $60,000. Calculate ROE.",
          "Why is the quick ratio considered more conservative than the current ratio?",
          "What does a gross profit margin of 35% mean?",
          "If net profit margin decreases from 20% to 15%, what might this indicate?"
        ],
        examTips: [
          "Know the formula for each ratio",
          "Understand what each ratio measures",
          "Be able to interpret results (good vs bad)",
          "Compare ratios to industry averages when possible"
        ]
      }
    ]
  }
};

// ==================== YEAR 12 ACCOUNTING NOTES ====================
export const year12AccountingStudentNotes = {
  strand1: {
    title: "Communicating Financial Information",
    overview: "Advanced internal control and audit processes for larger organizations.",
    image: accountingImages.accounting,
    units: [
      {
        id: "1.1",
        title: "Advanced Internal Controls",
        image: accountingImages.bank,
        objectives: [
          "Describe internal control over inventory",
          "Explain internal control over accounts receivable",
          "Describe control procedures for fixed assets",
          "Evaluate effectiveness of internal control systems"
        ],
        keyTerms: [
          { term: "Inventory Control", definition: "Procedures to safeguard stock, ensure accurate counts, and prevent theft or obsolescence." },
          { term: "Perpetual Inventory", definition: "A system that tracks inventory continuously with every transaction." },
          { term: "Physical Count", definition: "Manual counting of inventory to verify records." },
          { term: "Aging Schedule", definition: "A report classifying accounts receivable by how long they've been outstanding." },
          { term: "Credit Policy", definition: "Guidelines for extending credit to customers, including credit limits and payment terms." },
          { term: "Asset Register", definition: "A detailed record of all fixed assets including cost, depreciation, and location." }
        ],
        studyNotes: `
## Internal Control Over Inventory

### Objectives:
1. Safeguard inventory from theft
2. Ensure accurate records
3. Prevent obsolescence
4. Control purchasing

### Control Procedures:

| Procedure | Purpose |
|-----------|---------|
| Secure storage | Physical security |
| Limited access | Prevent theft |
| Perpetual records | Track movements |
| Regular counts | Verify accuracy |
| Segregation | Different staff order, receive, record |

### Inventory Count Procedures:
1. Schedule regular counts
2. Use trained counters
3. Prepare count sheets
4. Investigate discrepancies
5. Adjust records accordingly

## Internal Control Over Accounts Receivable

### Objectives:
1. Ensure all sales recorded
2. Minimize bad debts
3. Promote timely collection

### Control Procedures:

**Credit Approval:**
- Credit checks on new customers
- Set credit limits
- Authorization for exceeding limits

**Documentation:**
- Pre-numbered invoices
- Delivery notes signed
- Monthly statements sent

**Collection Controls:**
- Age analysis of receivables
- Follow-up procedures
- Written collection policies

### Aging Schedule Example:

| Customer | Current | 30 Days | 60 Days | 90+ | Total |
|----------|---------|---------|---------|-----|-------|
| ABC Ltd | $1,000 | $500 | $200 | - | $1,700 |
| XYZ Co | $800 | $300 | $100 | $400 | $1,600 |

## Internal Control Over Fixed Assets

### Objectives:
1. Safeguard assets from theft
2. Ensure proper maintenance
3. Maintain accurate depreciation records

### Control Procedures:
- Asset register with full details
- Regular physical verification
- Authorization for purchases and disposals
- Insurance coverage
        `,
        practiceQuestions: [
          "List FOUR control procedures for inventory.",
          "Explain the purpose of an aging schedule for accounts receivable.",
          "Why should different people be responsible for ordering, receiving, and recording inventory?",
          "What information should an asset register contain?",
          "Describe how a perpetual inventory system helps control inventory.",
          "A customer's account is 90 days overdue. What collection actions should be taken?"
        ],
        examTips: [
          "Link each control procedure to an internal control principle",
          "Understand the purpose behind each control",
          "Be ready to suggest improvements to weak control systems"
        ]
      }
    ]
  },
  strand2: {
    title: "Recording Financial Information",
    overview: "Advanced recording including inventory systems, GST, and subsidiary ledgers.",
    image: accountingImages.documents,
    units: [
      {
        id: "2.1",
        title: "Inventory Valuation",
        image: accountingImages.spreadsheet,
        objectives: [
          "Compare periodic and perpetual inventory systems",
          "Apply FIFO, LIFO, and weighted average methods",
          "Calculate cost of goods sold under each method",
          "Apply the lower of cost or net realizable value rule"
        ],
        keyTerms: [
          { term: "Periodic Inventory", definition: "System where inventory is counted physically at period end to determine COGS." },
          { term: "Perpetual Inventory", definition: "System that tracks inventory continuously with each transaction." },
          { term: "FIFO", definition: "First-In, First-Out - oldest items are sold first." },
          { term: "LIFO", definition: "Last-In, First-Out - newest items are sold first." },
          { term: "Weighted Average", definition: "Average cost calculated and applied to all units." },
          { term: "Net Realizable Value", definition: "Expected selling price less costs to complete and sell." },
          { term: "Lower of Cost or NRV", definition: "Inventory valued at the lower of original cost or net realizable value." }
        ],
        studyNotes: `
## Inventory Systems

### Periodic System:
- Count inventory at period end
- COGS = Opening + Purchases - Closing
- Simpler but less accurate

### Perpetual System:
- Track inventory continuously
- Know COGS after each sale
- More accurate but requires more resources

## Inventory Valuation Methods

### 1. FIFO (First-In, First-Out)
- Oldest items sold first
- Ending inventory at recent prices
- Higher profits when prices rise

### 2. LIFO (Last-In, First-Out)
- Newest items sold first
- Ending inventory at older prices
- Lower profits when prices rise

### 3. Weighted Average
\`\`\`
Average Cost = Total Cost ÷ Total Units
\`\`\`

### Example:
| Purchase | Units | Cost/Unit | Total |
|----------|-------|-----------|-------|
| Jan 1 | 100 | $10 | $1,000 |
| Jan 15 | 150 | $12 | $1,800 |
| Jan 25 | 100 | $14 | $1,400 |
| **Total** | **350** | | **$4,200** |

Sold: 200 units on Jan 31

**FIFO COGS:**
- 100 @ $10 = $1,000
- 100 @ $12 = $1,200
- Total COGS = $2,200

**LIFO COGS:**
- 100 @ $14 = $1,400
- 100 @ $12 = $1,200
- Total COGS = $2,600

**Weighted Average:**
- Avg cost = $4,200 ÷ 350 = $12
- COGS = 200 × $12 = $2,400

## Lower of Cost or NRV

Inventory must be valued at the lower of:
- Original cost, OR
- Net Realizable Value (NRV)

**Example:**
- Cost: $50 per unit
- NRV: $45 per unit
- Value inventory at: $45 per unit
        `,
        practiceQuestions: [
          "Explain the difference between periodic and perpetual inventory systems.",
          "Using FIFO, calculate COGS if: Opening inventory 50 @ $8, Purchases 100 @ $10, Sales 80 units.",
          "Why does FIFO typically show higher profits than LIFO when prices are rising?",
          "Calculate weighted average cost: 100 units @ $5, 200 units @ $6, 150 units @ $7.",
          "Inventory cost is $30 per unit but can only be sold for $25. At what value should it be recorded?",
          "List TWO advantages of a perpetual inventory system."
        ],
        examTips: [
          "FIFO = First costs go to COGS, recent costs stay in ending inventory",
          "LIFO = Recent costs go to COGS, first costs stay in ending inventory",
          "Weighted average = same cost for all units",
          "Always check if NRV is lower than cost"
        ]
      },
      {
        id: "2.2",
        title: "GST Accounting",
        image: accountingImages.money,
        objectives: [
          "Explain the concept and purpose of GST",
          "Record GST on sales and purchases",
          "Calculate GST payable or refundable",
          "Prepare GST returns"
        ],
        keyTerms: [
          { term: "GST", definition: "Goods and Services Tax - a consumption tax added to the price of goods and services." },
          { term: "GST Collected", definition: "GST charged to customers on sales (a liability)." },
          { term: "GST Paid", definition: "GST paid to suppliers on purchases (an asset)." },
          { term: "Net GST", definition: "GST Collected minus GST Paid - the amount payable to or refundable from the government." },
          { term: "Tax Invoice", definition: "A document showing the GST component separately." },
          { term: "Input Tax Credit", definition: "The GST paid on purchases that can be claimed back." }
        ],
        studyNotes: `
## How GST Works

GST is a consumption tax:
- Businesses collect GST from customers
- Businesses pay GST to suppliers
- Net difference is paid to (or refunded by) government

### GST Rate Example: 15%

**Calculating GST:**
\`\`\`
GST = Price × GST Rate
Price including GST = Price × (1 + GST Rate)
GST from inclusive price = Inclusive ÷ (1 + Rate) × Rate
\`\`\`

**Example:**
- Price excluding GST: $100
- GST (15%): $15
- Price including GST: $115

## Recording GST

### On Sales (GST Collected):
\`\`\`
Dr Accounts Receivable    $115
   Cr Sales Revenue            $100
   Cr GST Collected             $15
\`\`\`

### On Purchases (GST Paid):
\`\`\`
Dr Inventory             $100
Dr GST Paid               $15
   Cr Accounts Payable        $115
\`\`\`

## Calculating Net GST

| Description | Amount |
|-------------|--------|
| GST Collected | $5,000 |
| Less: GST Paid | ($3,500) |
| **Net GST Payable** | **$1,500** |

If GST Paid > GST Collected = GST Refund

## Paying GST to Government

\`\`\`
Dr GST Collected        $5,000
   Cr GST Paid                $3,500
   Cr Cash                    $1,500
\`\`\`
        `,
        practiceQuestions: [
          "Explain the purpose of GST.",
          "Calculate the GST on a purchase of $800 (GST rate 15%).",
          "Record the journal entry for a cash sale of $460 including GST.",
          "GST Collected is $8,000 and GST Paid is $5,500. Calculate net GST and state if payable or refund.",
          "Why is GST Collected a liability?",
          "Prepare the journal entry to pay net GST of $2,500 to the government."
        ],
        examTips: [
          "GST Collected = Liability (owed to government)",
          "GST Paid = Asset (claimable from government)",
          "Net GST = Collected - Paid",
          "Always separate GST from the actual sale/purchase amount"
        ]
      }
    ]
  },
  strand3: {
    title: "Reporting Financial Information",
    overview: "Preparing statements for not-for-profit organizations and partnerships.",
    image: accountingImages.financial,
    units: [
      {
        id: "3.1",
        title: "Not-for-Profit Accounting",
        image: accountingImages.documents,
        objectives: [
          "Distinguish between profit and not-for-profit organizations",
          "Prepare a Statement of Receipts and Payments",
          "Prepare an Income and Expenditure Account",
          "Account for subscriptions"
        ],
        keyTerms: [
          { term: "Not-for-Profit", definition: "An organization that operates for purposes other than making profit (e.g., clubs, charities, churches)." },
          { term: "Surplus", definition: "Excess of income over expenditure in a not-for-profit organization (equivalent to profit)." },
          { term: "Deficit", definition: "Excess of expenditure over income (equivalent to loss)." },
          { term: "Subscriptions", definition: "Regular payments by members to belong to the organization." },
          { term: "Accumulated Fund", definition: "The equivalent of owner's equity in a not-for-profit organization." },
          { term: "Statement of Receipts and Payments", definition: "A simple cash-based summary of money received and paid." }
        ],
        studyNotes: `
## Not-for-Profit vs Profit Organizations

| Feature | Profit | Not-for-Profit |
|---------|--------|----------------|
| Purpose | Earn profit | Service/Social |
| Owners | Shareholders | Members |
| Profit | Distributed | Reinvested |
| Equity | Owner's Equity | Accumulated Fund |

## Statement of Receipts and Payments

\`\`\`
STATEMENT OF RECEIPTS AND PAYMENTS
For the Year Ended [Date]

RECEIPTS
Opening Cash Balance         $X,XXX
Add: Subscriptions Received    X,XXX
     Donations                   XXX
     Fundraising Income          XXX
Total Receipts                $X,XXX

PAYMENTS
Rent Paid                    $X,XXX
Wages Paid                      XXX
Utilities Paid                  XXX
Equipment Purchased             XXX
Total Payments               (X,XXX)

CLOSING CASH BALANCE         $X,XXX
\`\`\`

## Subscription Accounting

### Subscriptions in Arrears (owing):
- Debit: Subscriptions Receivable
- Credit: Subscription Income

### Subscriptions in Advance (prepaid):
- Debit: Cash
- Credit: Subscriptions in Advance (liability)

### Example:
Members: 100 @ $50 each = $5,000 expected
- Received: $4,500
- In arrears: $500 (10 members didn't pay)
- In advance: $200 (4 members paid next year early)

\`\`\`
Subscription Income = Expected - Arrears at end + Arrears at start
                    + Advance at end - Advance at start
\`\`\`
        `,
        practiceQuestions: [
          "List THREE differences between profit and not-for-profit organizations.",
          "What is meant by 'accumulated fund'?",
          "A club has 200 members paying $30 each. 15 members haven't paid. Calculate subscription income.",
          "Explain subscriptions in arrears and subscriptions in advance.",
          "Prepare a Statement of Receipts and Payments from: Opening cash $500, Subscriptions received $3,000, Rent paid $1,200, Utilities $400.",
          "Why might a not-for-profit organization prefer cash-basis accounting?"
        ],
        examTips: [
          "Surplus = excess income (not 'profit')",
          "Deficit = excess expenditure (not 'loss')",
          "Arrears = asset (money owed TO the organization)",
          "Advance = liability (money owed BY the organization)"
        ]
      }
    ]
  },
  strand4: {
    title: "Evaluating Financial Information",
    overview: "Advanced ratio analysis and performance evaluation.",
    image: accountingImages.analysis,
    units: [
      {
        id: "4.1",
        title: "Advanced Ratio Analysis",
        image: accountingImages.financial,
        objectives: [
          "Calculate efficiency ratios",
          "Calculate debt management ratios",
          "Analyze trends over time",
          "Compare performance with industry benchmarks"
        ],
        keyTerms: [
          { term: "Inventory Turnover", definition: "COGS ÷ Average Inventory - how many times inventory is sold per year." },
          { term: "Receivables Turnover", definition: "Credit Sales ÷ Average Receivables - how quickly customers pay." },
          { term: "Average Collection Period", definition: "365 ÷ Receivables Turnover - average days to collect receivables." },
          { term: "Debt Ratio", definition: "Total Liabilities ÷ Total Assets - proportion of assets financed by debt." },
          { term: "Debt-to-Equity Ratio", definition: "Total Liabilities ÷ Owner's Equity - debt relative to owner investment." },
          { term: "Interest Coverage", definition: "Operating Profit ÷ Interest Expense - ability to pay interest." }
        ],
        studyNotes: `
## Efficiency Ratios

### 1. Inventory Turnover
\`\`\`
Inventory Turnover = COGS ÷ Average Inventory
Average Inventory = (Opening + Closing) ÷ 2
\`\`\`
- Higher = selling faster (good)
- Industry average varies

### 2. Days Inventory
\`\`\`
Days Inventory = 365 ÷ Inventory Turnover
\`\`\`
- Average days to sell inventory

### 3. Receivables Turnover
\`\`\`
Receivables Turnover = Credit Sales ÷ Average Receivables
\`\`\`
- Higher = collecting faster (good)

### 4. Average Collection Period
\`\`\`
Collection Period = 365 ÷ Receivables Turnover
\`\`\`
- Should be close to credit terms

## Debt Management Ratios

### 1. Debt Ratio
\`\`\`
Debt Ratio = Total Liabilities ÷ Total Assets
\`\`\`
- Lower than 50% is generally safer

### 2. Debt-to-Equity Ratio
\`\`\`
Debt-to-Equity = Total Liabilities ÷ Owner's Equity
\`\`\`
- Lower = less financial risk

### 3. Interest Coverage
\`\`\`
Interest Coverage = Operating Profit ÷ Interest Expense
\`\`\`
- Higher = better ability to pay interest
- Below 2:1 is risky

## Example:
- COGS: $200,000
- Average Inventory: $40,000
- Credit Sales: $300,000
- Average Receivables: $50,000
- Total Assets: $400,000
- Total Liabilities: $150,000
- Owner's Equity: $250,000

**Calculations:**
- Inventory Turnover = $200,000 ÷ $40,000 = 5 times
- Days Inventory = 365 ÷ 5 = 73 days
- Receivables Turnover = $300,000 ÷ $50,000 = 6 times
- Collection Period = 365 ÷ 6 = 61 days
- Debt Ratio = $150,000 ÷ $400,000 = 37.5%
- Debt-to-Equity = $150,000 ÷ $250,000 = 0.6:1
        `,
        practiceQuestions: [
          "Calculate inventory turnover if COGS = $180,000 and average inventory = $30,000.",
          "Receivables turnover is 8 times. Calculate the average collection period.",
          "Total assets are $500,000 and total liabilities are $200,000. Calculate the debt ratio.",
          "What does an inventory turnover of 12 times mean?",
          "A company's collection period is 45 days but credit terms are 30 days. What does this indicate?",
          "Why is the interest coverage ratio important to lenders?"
        ],
        examTips: [
          "Higher turnover = more efficient",
          "Lower collection period = faster payment",
          "Debt ratio > 50% = higher risk",
          "Compare ratios to previous years and industry averages"
        ]
      }
    ]
  }
};

// ==================== YEAR 13 ACCOUNTING NOTES ====================
export const year13AccountingStudentNotes = {
  strand1: {
    title: "Company Accounting",
    overview: "Accounting for companies including shares, dividends, and corporate governance.",
    image: accountingImages.accounting,
    units: [
      {
        id: "1.1",
        title: "Company Formation and Share Capital",
        image: accountingImages.money,
        objectives: [
          "Explain the characteristics of a company",
          "Distinguish between ordinary and preference shares",
          "Record share issues at par and at premium",
          "Account for bonus issues and share splits"
        ],
        keyTerms: [
          { term: "Company", definition: "A separate legal entity with limited liability for its shareholders." },
          { term: "Limited Liability", definition: "Shareholders' risk is limited to their investment in shares." },
          { term: "Ordinary Shares", definition: "Common shares with voting rights and variable dividends." },
          { term: "Preference Shares", definition: "Shares with fixed dividends and priority over ordinary shares." },
          { term: "Share Capital", definition: "The amount received from shareholders for shares issued." },
          { term: "Share Premium", definition: "The amount received above the par value of shares." },
          { term: "Bonus Issue", definition: "Free shares given to existing shareholders from retained earnings." },
          { term: "Authorized Capital", definition: "Maximum number of shares a company is allowed to issue." },
          { term: "Issued Capital", definition: "The number of shares actually sold to shareholders." }
        ],
        studyNotes: `
## Characteristics of a Company

1. **Separate Legal Entity** - Company is separate from owners
2. **Limited Liability** - Shareholders risk only their investment
3. **Perpetual Existence** - Company continues despite ownership changes
4. **Transferable Ownership** - Shares can be bought and sold
5. **Centralized Management** - Board of Directors manages

## Types of Shares

### Ordinary Shares (Common Stock)
- Voting rights
- Variable dividends
- Residual claim on assets
- Higher risk, higher potential return

### Preference Shares
- Fixed dividend rate
- Priority for dividends
- Usually no voting rights
- Can be cumulative or non-cumulative

## Recording Share Issues

### At Par Value ($1 per share):
\`\`\`
Dr Cash                     100,000
   Cr Share Capital              100,000
(Issue of 100,000 shares at $1 each)
\`\`\`

### At Premium ($1.50 per share, par $1):
\`\`\`
Dr Cash                     150,000
   Cr Share Capital              100,000
   Cr Share Premium               50,000
(Issue of 100,000 shares at $1.50)
\`\`\`

## Bonus Issues

Free shares from retained earnings.

**1:4 Bonus Issue** (1 free share for every 4 held):
- Existing shares: 400,000
- Bonus shares: 100,000
- Total after: 500,000

\`\`\`
Dr Retained Earnings        100,000
   Cr Share Capital              100,000
\`\`\`

No cash involved - just a transfer within equity.

## Share Splits

Increase number of shares, reduce value per share.
- 2:1 split: 100,000 shares become 200,000 shares
- Value per share halves
- Total equity unchanged
        `,
        practiceQuestions: [
          "List FOUR characteristics of a company.",
          "Explain the difference between ordinary and preference shares.",
          "A company issues 50,000 shares at $2.00 (par value $1.00). Prepare the journal entry.",
          "What is a bonus issue and why might a company issue bonus shares?",
          "A company has 200,000 shares. It makes a 1:5 bonus issue. How many shares after the bonus?",
          "Explain limited liability and its importance to shareholders."
        ],
        examTips: [
          "Share Premium = Amount paid ABOVE par value",
          "Bonus issues don't change total equity",
          "Par value × Number of shares = Share Capital",
          "Premium per share × Number of shares = Share Premium"
        ]
      },
      {
        id: "1.2",
        title: "Dividends and Reserves",
        image: accountingImages.financial,
        objectives: [
          "Distinguish between interim and final dividends",
          "Calculate and record dividend payments",
          "Explain different types of reserves",
          "Prepare dividend-related journal entries"
        ],
        keyTerms: [
          { term: "Dividend", definition: "Distribution of profits to shareholders." },
          { term: "Interim Dividend", definition: "Dividend paid during the financial year." },
          { term: "Final Dividend", definition: "Dividend declared at year-end, requiring shareholder approval." },
          { term: "Dividend per Share", definition: "Total dividend ÷ Number of shares." },
          { term: "Reserves", definition: "Retained profits set aside for specific purposes." },
          { term: "General Reserve", definition: "Reserve for general business purposes." },
          { term: "Retained Earnings", definition: "Accumulated profits not distributed as dividends." }
        ],
        studyNotes: `
## Types of Dividends

### Interim Dividend
- Paid during the year
- Based on expected profits
- Declared by Directors

### Final Dividend
- Declared after year-end
- Requires shareholder approval
- Based on actual profits

## Dividend Calculations

\`\`\`
Total Dividend = Number of Shares × Dividend per Share
Dividend per Share = Total Dividend ÷ Number of Shares
Dividend Rate = (DPS ÷ Par Value) × 100
\`\`\`

### Example:
- 1,000,000 ordinary shares
- Interim dividend: 5 cents per share
- Final dividend: 8 cents per share

Total dividends:
- Interim: 1,000,000 × $0.05 = $50,000
- Final: 1,000,000 × $0.08 = $80,000
- Total: $130,000

## Recording Dividends

### Interim Dividend (paid immediately):
\`\`\`
Dr Retained Earnings         50,000
   Cr Cash                        50,000
\`\`\`

### Final Dividend (declaration):
\`\`\`
Dr Retained Earnings         80,000
   Cr Dividends Payable           80,000
\`\`\`

### Final Dividend (payment):
\`\`\`
Dr Dividends Payable         80,000
   Cr Cash                        80,000
\`\`\`

## Types of Reserves

### Revenue Reserves (distributable):
- **Retained Earnings** - Undistributed profits
- **General Reserve** - Set aside for general purposes

### Capital Reserves (non-distributable):
- **Share Premium** - From share issues above par
- **Revaluation Reserve** - From asset revaluations

## Transfer to General Reserve:
\`\`\`
Dr Retained Earnings         20,000
   Cr General Reserve             20,000
\`\`\`
        `,
        practiceQuestions: [
          "Explain the difference between interim and final dividends.",
          "A company has 500,000 shares and declares a dividend of 10 cents per share. Calculate total dividend.",
          "Why does a final dividend require shareholder approval but interim dividend doesn't?",
          "Prepare journal entries for: (a) Interim dividend of $25,000 paid, (b) Final dividend of $40,000 declared.",
          "Distinguish between revenue reserves and capital reserves.",
          "Why might a company transfer profits to general reserve instead of paying dividends?"
        ],
        examTips: [
          "Interim = paid now, Final = declared then paid later",
          "Revenue reserves = can be distributed as dividends",
          "Capital reserves = cannot be distributed",
          "Dividends are NOT expenses - they're distributions of profit"
        ]
      }
    ]
  },
  strand2: {
    title: "Advanced Recording",
    overview: "Complex adjustments including bad debts, asset disposals, and revaluations.",
    image: accountingImages.documents,
    units: [
      {
        id: "2.1",
        title: "Bad Debts and Provisions",
        image: accountingImages.money,
        objectives: [
          "Account for bad debts written off",
          "Create and adjust provision for doubtful debts",
          "Account for recovery of bad debts",
          "Apply appropriate estimation methods"
        ],
        keyTerms: [
          { term: "Bad Debt", definition: "An amount owed to the business that cannot be collected." },
          { term: "Bad Debts Expense", definition: "The cost of uncollectible accounts (an expense)." },
          { term: "Allowance for Doubtful Debts", definition: "An estimate of receivables that may not be collected (contra-asset)." },
          { term: "Direct Write-off", definition: "Method where bad debts are expensed when identified as uncollectible." },
          { term: "Allowance Method", definition: "Method where bad debts are estimated at year-end." },
          { term: "Recovery of Bad Debt", definition: "Collection of a debt previously written off." }
        ],
        studyNotes: `
## Bad Debts - Direct Write-Off

When a specific debt becomes uncollectible:

\`\`\`
Dr Bad Debts Expense         500
   Cr Accounts Receivable        500
(Write off ABC Ltd debt as uncollectible)
\`\`\`

## Allowance Method (Preferred)

### Creating the Allowance:
End of year, estimate uncollectible accounts.

\`\`\`
Dr Bad Debts Expense       3,000
   Cr Allowance for Doubtful Debts    3,000
(Provide 5% of $60,000 receivables)
\`\`\`

### Adjusting the Allowance:

**Increase needed:**
Required allowance: $3,500
Existing allowance: $2,000
Increase: $1,500

\`\`\`
Dr Bad Debts Expense       1,500
   Cr Allowance for Doubtful Debts    1,500
\`\`\`

**Decrease needed:**
Required: $2,500
Existing: $3,500
Decrease: $1,000

\`\`\`
Dr Allowance for Doubtful Debts  1,000
   Cr Bad Debts Expense              1,000
\`\`\`

### Writing Off Against Allowance:

\`\`\`
Dr Allowance for Doubtful Debts   500
   Cr Accounts Receivable             500
(Specific debt written off)
\`\`\`

## Recovery of Bad Debt

If a written-off debt is later collected:

**Step 1 - Reinstate the debt:**
\`\`\`
Dr Accounts Receivable        300
   Cr Bad Debts Recovered         300
\`\`\`

**Step 2 - Record the payment:**
\`\`\`
Dr Cash                       300
   Cr Accounts Receivable         300
\`\`\`

## Statement Presentation

\`\`\`
Accounts Receivable        $60,000
Less: Allowance            (3,000)
                          --------
Net Receivables           $57,000
\`\`\`
        `,
        practiceQuestions: [
          "Explain the difference between the direct write-off method and the allowance method.",
          "Accounts Receivable is $80,000. Management wants a 5% allowance. The existing allowance is $3,000. Calculate the adjustment needed.",
          "Prepare the journal entry to write off $600 owed by XYZ Ltd as uncollectible.",
          "A $400 debt written off last year has been collected. Prepare the journal entries.",
          "Why is the allowance method considered more appropriate than direct write-off?",
          "How is the allowance for doubtful debts presented on the statement of financial position?"
        ],
        examTips: [
          "Allowance is a contra-asset (reduces receivables)",
          "Bad Debts Expense appears on Income Statement",
          "Always calculate the ADJUSTMENT needed, not the total allowance",
          "Recovery = reinstate then collect"
        ]
      },
      {
        id: "2.2",
        title: "Asset Disposal and Revaluation",
        image: accountingImages.calculator,
        objectives: [
          "Calculate gain or loss on asset disposal",
          "Record disposal of non-current assets",
          "Account for asset revaluations",
          "Prepare journal entries for trade-ins"
        ],
        keyTerms: [
          { term: "Book Value", definition: "Cost of asset minus accumulated depreciation." },
          { term: "Gain on Disposal", definition: "When selling price exceeds book value." },
          { term: "Loss on Disposal", definition: "When selling price is less than book value." },
          { term: "Revaluation", definition: "Adjusting an asset's carrying value to fair market value." },
          { term: "Revaluation Surplus", definition: "Increase in asset value recorded in equity." },
          { term: "Trade-In", definition: "Exchanging an old asset as part payment for a new asset." }
        ],
        studyNotes: `
## Asset Disposal

### Calculating Gain/Loss:

\`\`\`
Book Value = Cost - Accumulated Depreciation
Gain/Loss = Selling Price - Book Value
\`\`\`

### Example 1 - Gain on Disposal:
- Equipment cost: $50,000
- Accumulated depreciation: $35,000
- Book value: $15,000
- Sold for: $18,000
- Gain: $3,000

\`\`\`
Dr Cash                      18,000
Dr Accumulated Depreciation  35,000
   Cr Equipment                   50,000
   Cr Gain on Disposal             3,000
\`\`\`

### Example 2 - Loss on Disposal:
- Sold for: $12,000
- Loss: $3,000

\`\`\`
Dr Cash                      12,000
Dr Accumulated Depreciation  35,000
Dr Loss on Disposal           3,000
   Cr Equipment                   50,000
\`\`\`

## Asset Revaluation

When fair value differs from book value:

### Revaluation Increase:
- Book value: $40,000
- Revalued to: $55,000
- Increase: $15,000

\`\`\`
Dr Asset                     15,000
   Cr Revaluation Surplus         15,000
\`\`\`

### Trade-In

Old equipment (book value $8,000) traded in for new equipment ($25,000). Trade-in allowance $10,000, cash paid $15,000.

\`\`\`
Dr Equipment (new)           25,000
Dr Accumulated Depreciation  XX,XXX
   Cr Equipment (old)            XX,XXX
   Cr Cash                       15,000
   Cr Gain on Trade-in            2,000
\`\`\`

Gain = Trade-in value $10,000 - Book value $8,000 = $2,000
        `,
        practiceQuestions: [
          "Equipment cost $40,000, accumulated depreciation $28,000, sold for $15,000. Calculate gain or loss.",
          "Prepare the journal entry for the disposal in Q1.",
          "A building with book value of $200,000 is revalued to $280,000. Prepare the journal entry.",
          "Explain the difference between a gain on disposal and a revaluation surplus.",
          "Old machinery (cost $30,000, acc. dep. $22,000) is traded in for new machinery worth $45,000. Trade-in allowance is $9,000. Prepare the journal entry.",
          "Why might a company revalue its assets?"
        ],
        examTips: [
          "Book Value = Cost - Accumulated Depreciation",
          "Gain = Sold for MORE than book value",
          "Loss = Sold for LESS than book value",
          "Revaluation surplus goes to equity, not income"
        ]
      }
    ]
  },
  strand3: {
    title: "Company Financial Statements",
    overview: "Preparing and presenting company financial statements.",
    image: accountingImages.financial,
    units: [
      {
        id: "3.1",
        title: "Company Statement of Financial Position",
        image: accountingImages.spreadsheet,
        objectives: [
          "Prepare a company statement of financial position",
          "Present share capital and reserves correctly",
          "Calculate and present earnings per share",
          "Prepare a statement of changes in equity"
        ],
        keyTerms: [
          { term: "Shareholders' Equity", definition: "The total of share capital plus reserves in a company." },
          { term: "Earnings Per Share (EPS)", definition: "Net profit after tax divided by number of ordinary shares." },
          { term: "Statement of Changes in Equity", definition: "A statement showing movements in all equity items during the period." },
          { term: "Minority Interest", definition: "The portion of equity belonging to shareholders who don't have control." }
        ],
        studyNotes: `
## Company Statement of Financial Position

\`\`\`
STATEMENT OF FINANCIAL POSITION
As at December 31, 2026

ASSETS
Non-Current Assets:
Property, Plant & Equipment    $XXX,XXX
Intangible Assets                XX,XXX
                               --------
Total Non-Current Assets       $XXX,XXX

Current Assets:
Inventory                      $XX,XXX
Trade Receivables               XX,XXX
Less: Allowance                (X,XXX)
Cash                            XX,XXX
                               --------
Total Current Assets           $XX,XXX

TOTAL ASSETS                   $XXX,XXX

EQUITY AND LIABILITIES
Shareholders' Equity:
Share Capital                  $XX,XXX
Share Premium                   XX,XXX
Revaluation Surplus              X,XXX
Retained Earnings               XX,XXX
                               --------
Total Equity                   $XXX,XXX

Non-Current Liabilities:
Bank Loan                      $XX,XXX

Current Liabilities:
Trade Payables                 $XX,XXX
Dividends Payable                X,XXX
Tax Payable                      X,XXX
                               --------
Total Current Liabilities      $XX,XXX

TOTAL EQUITY & LIABILITIES     $XXX,XXX
\`\`\`

## Earnings Per Share (EPS)

\`\`\`
EPS = Net Profit After Tax ÷ Number of Ordinary Shares
\`\`\`

Example:
- Net profit after tax: $250,000
- Ordinary shares: 500,000
- EPS = $250,000 ÷ 500,000 = $0.50 per share

## Statement of Changes in Equity

\`\`\`
                    Share    Share   Reval.  Retained
                   Capital  Premium Surplus Earnings  Total
                   -------  ------- ------- --------  -----
Opening Balance   $200,000  $30,000  $5,000  $80,000 $315,000
Profit for year         -        -       -   60,000   60,000
Revaluation             -        -  10,000        -   10,000
New shares issued  50,000   10,000       -        -   60,000
Dividends               -        -       -  (25,000) (25,000)
                  -------  ------- -------  -------  -------
Closing Balance   $250,000  $40,000 $15,000 $115,000 $420,000
\`\`\`
        `,
        practiceQuestions: [
          "Net profit after tax is $180,000. There are 600,000 ordinary shares. Calculate EPS.",
          "Explain what the statement of changes in equity shows.",
          "A company has: Share Capital $300,000, Share Premium $50,000, Revaluation Surplus $20,000, Retained Earnings $130,000. Calculate total shareholders' equity.",
          "Where does 'dividends payable' appear on the statement of financial position?",
          "Prepare a statement of changes in equity showing: Opening retained earnings $50,000, Profit $35,000, Dividends $15,000.",
          "Why is EPS important to investors?"
        ],
        examTips: [
          "EPS uses profit AFTER TAX",
          "Statement of Changes shows movements in ALL equity items",
          "Dividends reduce retained earnings",
          "Share capital + Reserves = Total Equity"
        ]
      }
    ]
  },
  strand4: {
    title: "Investment Analysis",
    overview: "Evaluating companies using investment ratios and valuation methods.",
    image: accountingImages.analysis,
    units: [
      {
        id: "4.1",
        title: "Investment Ratios",
        image: accountingImages.financial,
        objectives: [
          "Calculate key investment ratios",
          "Interpret investment ratios for decision making",
          "Compare companies using ratio analysis",
          "Understand business valuation methods"
        ],
        keyTerms: [
          { term: "Price-Earnings Ratio (P/E)", definition: "Market price per share divided by EPS - shows how much investors pay for each dollar of earnings." },
          { term: "Dividend Yield", definition: "Dividend per share divided by market price - shows return from dividends." },
          { term: "Dividend Cover", definition: "EPS divided by dividend per share - shows how many times earnings cover dividends." },
          { term: "Net Asset Value (NAV)", definition: "Net assets (equity) divided by number of shares." },
          { term: "Market Capitalization", definition: "Total market value of all shares (price × shares)." }
        ],
        studyNotes: `
## Investment Ratios

### 1. Earnings Per Share (EPS)
\`\`\`
EPS = Net Profit After Tax ÷ Ordinary Shares
\`\`\`

### 2. Price-Earnings Ratio (P/E)
\`\`\`
P/E = Market Price per Share ÷ EPS
\`\`\`
- Higher P/E = investors expect higher growth
- Compare to industry average

### 3. Dividend Yield
\`\`\`
Dividend Yield = (DPS ÷ Market Price) × 100
\`\`\`
- Higher yield = better income return
- Compare to bank interest rates

### 4. Dividend Cover
\`\`\`
Dividend Cover = EPS ÷ DPS
\`\`\`
- Higher = more sustainable dividends
- Below 1 = paying more than earning

### 5. Net Asset Value per Share
\`\`\`
NAV = Net Assets ÷ Number of Shares
\`\`\`
- Compare to market price

## Example Analysis:

| Data | Amount |
|------|--------|
| Net Profit After Tax | $500,000 |
| Ordinary Shares | 2,000,000 |
| Market Price | $3.50 |
| Dividend per Share | $0.15 |
| Net Assets | $6,000,000 |

**Calculations:**
- EPS = $500,000 ÷ 2,000,000 = **$0.25**
- P/E = $3.50 ÷ $0.25 = **14 times**
- Dividend Yield = ($0.15 ÷ $3.50) × 100 = **4.3%**
- Dividend Cover = $0.25 ÷ $0.15 = **1.67 times**
- NAV = $6,000,000 ÷ 2,000,000 = **$3.00**

## Business Valuation Methods

### 1. Net Asset Value Method
\`\`\`
Value = Total Assets - Total Liabilities
\`\`\`

### 2. Earnings-Based
\`\`\`
Value = Maintainable Earnings × P/E Multiple
\`\`\`

### 3. Dividend-Based
\`\`\`
Value = Expected Dividend ÷ Required Return
\`\`\`

### 4. Discounted Cash Flow
\`\`\`
Value = Sum of (Future Cash Flows ÷ (1 + r)^n)
\`\`\`
        `,
        practiceQuestions: [
          "Calculate P/E ratio if market price is $8 and EPS is $0.50.",
          "EPS is $0.40 and DPS is $0.15. Calculate dividend cover and comment on sustainability.",
          "A company's shares trade at $5.00 with NAV of $4.00. What does this indicate?",
          "Dividend yield is 3% and market price is $6.00. Calculate the dividend per share.",
          "Compare two companies: Company A has P/E of 8, Company B has P/E of 20. What might explain the difference?",
          "A company earns $200,000 annually. Industry P/E is 10. Value the company using the earnings method."
        ],
        examTips: [
          "P/E = Price investors pay for $1 of earnings",
          "Higher P/E = higher growth expectations",
          "Dividend Cover > 1 = dividends are sustainable",
          "NAV < Market Price = market values growth potential"
        ]
      }
    ]
  }
};
